import { CheckOutlined, CloseOutlined, LogoutOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Space, Tag, Typography } from "antd";
import type { TableProps } from "antd";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ApiError, apiFetch } from "../api/client";
import { compareText } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type { ListResponse, NotificationsResponse, PendingInvitation } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { DataTable } from "../components/DataTable";
import { LanguageSwitcher } from "../layout/LanguageSwitcher";

/** InvitationHelper::STATUS_PENDING — accepted, but only effective from valid_from on. */
const STATUS_PENDING = 20;

/**
 * A cell for free text of unknown length.
 *
 * The table sizes its columns to their content (scroll: max-content), so a long
 * sentence would stretch its column across the screen and push the buttons out
 * of sight. The cap has to sit on the text rather than on the column, because a
 * column `width` is only a hint the content can overrule.
 */
function wrapped(width: number) {
  return (text: string | null) =>
    text ? (
      <Typography.Text style={{ display: "inline-block", maxWidth: width, whiteSpace: "normal" }}>
        {text}
      </Typography.Text>
    ) : null;
}

/**
 * The invitations waiting for an answer, shown right after login when
 * GET /main/index reports pending_invitations.
 *
 * Like the privacy policy this renders without the shell, because it is a step
 * on the way in rather than a place in the navigation. It is not a gate though:
 * the API blocks nothing here, so "go to the main page" simply puts the screen
 * away for this login — the invitations stay open until they are confirmed or
 * declined, exactly as in the old application.
 */
export function ConfirmChangesPage() {
  const { t } = useTranslation();
  const { logout, dismissPendingInvitations } = useAuth();
  const navigate = useNavigate();
  const notify = useServerNotifications();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  const invitations = useQuery({
    queryKey: ["main", "confirm_changes"],
    queryFn: () => apiFetch<ListResponse<PendingInvitation>>("/main/confirm_changes"),
  });

  // Answering an invitation can hand out or take away a role, which changes the
  // navigation and the badge counts — so the sidebar and the landing state go
  // stale along with the list.
  const refresh = useCallback(
    (payload: NotificationsResponse) => {
      notify(payload.notifications);
      void queryClient.invalidateQueries({ queryKey: ["main", "confirm_changes"] });
      void queryClient.invalidateQueries({ queryKey: ["main", "index"] });
      void queryClient.invalidateQueries({ queryKey: ["sidebar"] });
    },
    [notify, queryClient],
  );

  const confirmInvitation = useMutation({
    mutationFn: (id: number) =>
      apiFetch<NotificationsResponse>(`/main/invitations/${id}/confirm`, { method: "PUT" }),
    onSuccess: refresh,
    onError: showError,
  });

  const declineInvitation = useMutation({
    mutationFn: (id: number) =>
      apiFetch<NotificationsResponse>(`/main/invitations/${id}`, { method: "DELETE" }),
    onSuccess: refresh,
    onError: showError,
  });

  const askAndDecline = useCallback(
    (id: number) => {
      // Declining destroys the invitation and mails the author — the old
      // application asked first, and so does this one.
      modal.confirm({
        title: t("dialog.confirmation_title"),
        content: t("invitation.confirm_decline_invitation"),
        okText: t("dialog.confirm"),
        okButtonProps: { danger: true },
        cancelText: t("dialog.cancel"),
        onOk: () => declineInvitation.mutate(id),
      });
    },
    [modal, t, declineInvitation],
  );

  const busy = confirmInvitation.isPending || declineInvitation.isPending;

  const columns = useMemo<TableProps<PendingInvitation>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.invitation.status"),
        dataIndex: "status_label",
        width: 120,
        render: (label: string, row) => (
          // The grid coloured this cell: blue for an invitation that only takes
          // effect later, plain for one that applies immediately.
          <Tag color={row.status === STATUS_PENDING ? "blue" : "default"}>{label}</Tag>
        ),
        sorter: (a, b) => compareText(a.status_label, b.status_label),
      },
      {
        title: t("invitation.pending_invitations"),
        dataIndex: "message",
        // A whole sentence naming the role, the congregation and who is
        // inviting — several lines long in the worst case.
        width: 420,
        render: wrapped(420),
      },
      {
        title: t("activerecord.attributes.invitation.email"),
        dataIndex: "author_email",
        render: (value: string | null) =>
          value ? <Typography.Link href={`mailto:${value}`}>{value}</Typography.Link> : null,
      },
      {
        title: t("activerecord.attributes.invitation.phone"),
        dataIndex: "author_phone",
        render: (value: string | null) =>
          value ? <Typography.Link href={`tel:${value}`}>{value}</Typography.Link> : null,
      },
      {
        title: t("activerecord.attributes.user.mobile"),
        dataIndex: "author_mobile",
        render: (value: string | null) =>
          value ? <Typography.Link href={`tel:${value}`}>{value}</Typography.Link> : null,
      },
      {
        title: t("activerecord.attributes.invitation.observations"),
        dataIndex: "observations",
        // Whatever the author typed into the assignment dialog, so equally
        // open-ended — narrower than the message, which is the row's subject.
        width: 320,
        render: wrapped(320),
      },
      {
        key: "actions",
        fixed: "right",
        width: 220,
        render: (_value, row) => (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              disabled={busy}
              onClick={() => confirmInvitation.mutate(row.id)}
            >
              {t("invitation.confirm")}
            </Button>
            <Button
              danger
              size="small"
              icon={<CloseOutlined />}
              disabled={busy}
              onClick={() => askAndDecline(row.id)}
            >
              {t("invitation.decline")}
            </Button>
          </Space>
        ),
      },
    ],
    [t, busy, confirmInvitation, askAndDecline],
  );

  const goToMainPage = () => {
    dismissPendingInvitations();
    navigate("/", { replace: true });
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: 24,
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
      }}
    >
      <Card
        style={{ width: "100%", maxWidth: 1100 }}
        title={t("invitation.pending_invitations")}
        extra={
          <Space>
            <LanguageSwitcher />
            <Button icon={<LogoutOutlined />} onClick={() => void logout()}>
              {t("auth.logout")}
            </Button>
          </Space>
        }
      >
        <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
          <Typography.Text type="secondary">
            {t("invitation.pending_invitations_info")}
          </Typography.Text>

          <DataTable<PendingInvitation>
            columns={columns}
            rows={invitations.data?.data}
            loading={invitations.isPending || busy}
            error={invitations.error}
            paginated={false}
          />

          <Button type="primary" onClick={goToMainPage}>
            {t("auth.access_main_page")}
          </Button>
        </Space>
      </Card>
    </div>
  );
}
